/**
 * @file: CampaignRewardDepositedBody.tsx
 *
 * `OrgCampaignRewardDeposited` — a campaign (funder-driven incentive)
 * reward landed in the member's account. Distinct from product-referral
 * commission: prominent orange +amount + resulting balance, then a
 * per-tx breakdown rendered like the account-detail page — tx-type label
 * colored by kind, plus the campaign context (campaign name / plate)
 * parsed out of the snapshot's raw `extra` JSON so the user can tell
 * which activity and vehicle the reward traces back to.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMemberAccountTxDetail } from '@euler/model/OrgMemberAccountTxDetail';
import {
  OrgCampaignRewardDepositedFeedPayload,
  OrgMemberAccountTxSnapshot,
} from '@euler/model/feed';
import { FC } from 'react';
import { View } from 'react-native';
import { formatYuan, txTypeInfo } from './common/labels';

export const CampaignRewardDepositedBody: FC<{
  p: OrgCampaignRewardDepositedFeedPayload;
}> = ({ p }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View>
      <View
        css={`
          flex-direction: row;
          align-items: baseline;
        `}
      >
        <Typography.Text size={rpx(22)} color="#fa8c16" bold>
          +¥{formatYuan(p.amount)}
        </Typography.Text>
        <Typography.Text ml={rpx(8)} size={rpx(12)} color="#6b7280">
          活动奖励已入账
        </Typography.Text>
      </View>
      <Typography.Text mt={rpx(4)} size={rpx(12)} color="#6b7280">
        账户余额 ¥{formatYuan(p.balance)}
      </Typography.Text>
      {p.transactions && p.transactions.length > 0 ? (
        <View
          css={`
            margin-top: ${rpx(8)}px;
          `}
        >
          {p.transactions.map((tx, idx) => (
            <View
              key={String(tx.id)}
              css={`
                ${idx > 0
                  ? `border-top-width: 1px; border-top-color: #e5e7eb;`
                  : ''}
              `}
            >
              <TxBreakdownRow tx={tx} />
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
};

interface CampaignRewardLine {
  campaignName: string;
  licensePlateNo: string | null;
}

/**
 * Parse the snapshot's raw `extra` into the campaign display fields
 * shared by the reward and reward-share variants. Returns `undefined`
 * on missing / malformed / non-campaign extras so the row degrades to
 * title + amount only.
 */
function parseTxDetail(
  extra: string | undefined,
): OrgMemberAccountTxDetail | undefined {
  if (!extra) return undefined;
  try {
    return JSON.parse(extra) as OrgMemberAccountTxDetail;
  } catch {
    return undefined;
  }
}

function campaignLineOf(
  extra: string | undefined,
): CampaignRewardLine | undefined {
  const detail = parseTxDetail(extra);
  if (detail == null) return undefined;
  if (detail.type === 'campaignReward') {
    return {
      campaignName: detail.campaignReward.campaignName,
      licensePlateNo: detail.campaignReward.licensePlateNo,
    };
  }
  if (detail.type === 'campaignRewardShare') {
    return {
      campaignName: detail.campaignRewardShare.campaignName,
      licensePlateNo: detail.campaignRewardShare.licensePlateNo,
    };
  }
  return undefined;
}

const TxBreakdownRow: FC<{ tx: OrgMemberAccountTxSnapshot }> = ({ tx }) => {
  const { rpx } = useResponsiveContext();
  const ti = txTypeInfo(tx.txType);
  const line = campaignLineOf(tx.extra);
  return (
    <View
      css={`
        padding: ${rpx(8)}px 0;
      `}
    >
      <View
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: baseline;
        `}
      >
        <Typography.Text size={rpx(13)} color={ti.color} bold>
          {ti.text}
        </Typography.Text>
        <Typography.Text size={rpx(13)} color={ti.color} medium>
          +¥{formatYuan(tx.amount)}
        </Typography.Text>
      </View>
      {line ? (
        <View
          css={`
            margin-top: ${rpx(4)}px;
          `}
        >
          <DetailLine label="活动名称" value={line.campaignName} />
          <DetailLine label="车牌号码" value={line.licensePlateNo} />
        </View>
      ) : null}
      {tx.remark ? (
        <Typography.Text
          mt={rpx(4)}
          size={rpx(11)}
          color="#6b7280"
          numberOfLines={2}
        >
          {tx.remark}
        </Typography.Text>
      ) : null}
    </View>
  );
};

const DetailLine: FC<{
  label: string;
  value: string | null | undefined;
}> = ({ label, value }) => {
  const { rpx } = useResponsiveContext();
  if (!value) return null;
  return (
    <View
      css={`
        flex-direction: row;
        margin-top: ${rpx(2)}px;
      `}
    >
      <Typography.Text size={rpx(12)} color="#6b7280">
        {label}: {value}
      </Typography.Text>
    </View>
  );
};
